/**
 * Lead time — "order within 2 hr 10 min and it ships today".
 *
 * The rule: an order placed before the cutoff on a business day ships the
 * same day; anything later, or on a weekend or listed holiday, ships the next
 * business day. A part with handling time (request 178: the
 * fixmytoys.handletime metafield, in business days) ships that many business
 * days after the order's day — Monday before the cutoff plus three is
 * Thursday; Monday after it counts from Tuesday and is Friday. The cutoff,
 * time zone, holidays and handling time come in as data attributes on the
 * element (snippets/lead-time.liquid).
 *
 * All of the clock work is done in the warehouse's zone through Intl, so a
 * customer in Phoenix sees the same answer as one in Boston, and daylight
 * saving takes care of itself. Nothing here assumes the browser's zone.
 *
 * Liquid renders the static rule ("Orders placed by 2:30 PM ET...") so the
 * line is meaningful before this runs, and without JS at all.
 */
(function () {
  'use strict';

  const DAY = 86400000;

  /** The wall-clock date and time in `zone`. */
  function clockIn(zone, date) {
    const parts = {};
    for (const part of new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).formatToParts(date)) {
      parts[part.type] = part.value;
    }
    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      minutes: Number(parts.hour) * 60 + Number(parts.minute),
    };
  }

  const pad = (n) => String(n).padStart(2, '0');

  /** A calendar date is carried as UTC midnight, so day arithmetic is exact. */
  function stamp(utcDay) {
    return `${utcDay.getUTCFullYear()}-${pad(utcDay.getUTCMonth() + 1)}-${pad(utcDay.getUTCDate())}`;
  }

  /**
   * The warehouse's fixed holidays (Lucas, 2026-10-14, request 133): on a
   * weekday these are not business days; on a weekend nothing moves (no
   * "observed" Monday or Friday). Memorial Day, Labor Day and Thanksgiving
   * are always weekdays; the dated ones count only when they land on one.
   * The theme setting adds any other date.
   */
  function nthWeekdayOf(year, month, weekday, n) {
    // month 1–12; weekday 0–6 (Sunday 0); n ≥ 1 from the start, -1 for the last.
    if (n > 0) {
      const first = new Date(Date.UTC(year, month - 1, 1));
      const offset = (weekday - first.getUTCDay() + 7) % 7;
      return new Date(Date.UTC(year, month - 1, 1 + offset + (n - 1) * 7));
    }
    const last = new Date(Date.UTC(year, month, 0));
    const back = (last.getUTCDay() - weekday + 7) % 7;
    return new Date(Date.UTC(year, month - 1, last.getUTCDate() - back));
  }

  function fixedHolidays(year) {
    return [
      new Date(Date.UTC(year, 0, 1)), // New Year's Day
      nthWeekdayOf(year, 5, 1, -1), // Memorial Day: last Monday of May
      new Date(Date.UTC(year, 6, 4)), // Independence Day
      nthWeekdayOf(year, 9, 1, 1), // Labor Day: first Monday of September
      nthWeekdayOf(year, 11, 4, 4), // Thanksgiving: fourth Thursday of November
      new Date(Date.UTC(year, 11, 25)), // Christmas Day
    ].map(stamp);
  }

  function isBusinessDay(utcDay, holidays) {
    const weekday = utcDay.getUTCDay();
    if (weekday === 0 || weekday === 6) return false;
    const day = stamp(utcDay);
    return !holidays.includes(day) && !fixedHolidays(utcDay.getUTCFullYear()).includes(day);
  }

  function nextBusinessDay(utcDay, holidays) {
    let day = utcDay;
    do {
      day = new Date(day.getTime() + DAY);
    } while (!isBusinessDay(day, holidays));
    return day;
  }

  /**
   * @param {Date} now
   * @param {{cutoff?: string, zone?: string, holidays?: string[], handleDays?: number}} options
   *   cutoff "HH:MM" in 24-hour time, zone an IANA name, holidays "YYYY-MM-DD",
   *   handleDays the part's handling time in business days (0 when absent).
   * @returns {{shipsToday: boolean, inWindow: boolean, minutesLeft: number, shipDate: Date, handleDays: number}}
   *   inWindow: the cutoff is still ahead today, so ordering now counts as
   *   today. shipsToday is inWindow with no handling time. shipDate is the
   *   ship day as UTC midnight; format it with timeZone 'UTC'.
   */
  function compute(now, options) {
    const opts = options || {};
    const zone = opts.zone || 'America/New_York';
    const holidays = opts.holidays || []; // extra dates from the setting; the fixed ones are built in
    const [hour, minute] = String(opts.cutoff || '14:30').split(':').map(Number);
    if (!Number.isFinite(hour)) throw new Error('bad cutoff');
    const cutoff = hour * 60 + (minute || 0);
    const handleDays = Math.max(0, Math.floor(Number(opts.handleDays) || 0));

    const clock = clockIn(zone, now);
    let day = new Date(Date.UTC(clock.year, clock.month - 1, clock.day));

    // The order's day: today while the cutoff is ahead on a business day,
    // otherwise the next business day. Handling time counts on from there.
    const inWindow = isBusinessDay(day, holidays) && clock.minutes < cutoff;
    if (!inWindow) day = nextBusinessDay(day, holidays);
    for (let i = 0; i < handleDays; i += 1) day = nextBusinessDay(day, holidays);

    return {
      shipsToday: inWindow && handleDays === 0,
      inWindow,
      minutesLeft: inWindow ? cutoff - clock.minutes : 0,
      shipDate: day,
      handleDays,
    };
  }

  /** "Friday, October 23rd" (Lucas, 2026-10-20, request 180). */
  function ordinal(n) {
    const tens = n % 100;
    if (tens >= 11 && tens <= 13) return `${n}th`;
    return `${n}${['th', 'st', 'nd', 'rd'][n % 10] || 'th'}`;
  }

  function longDate(utcDay) {
    const weekday = utcDay.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
    const month = utcDay.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
    return `${weekday}, ${month} ${ordinal(utcDay.getUTCDate())}`;
  }

  function describe(result, cutoffLabel) {
    const days = result.handleDays || 0;
    const countdown = () => {
      const hours = Math.floor(result.minutesLeft / 60);
      const minutes = result.minutesLeft % 60;
      return hours ? `${hours} hr ${minutes} min` : `${minutes} min`;
    };
    if (result.shipsToday) return `Order within ${countdown()} and it ships today.`;
    const estimate = `Estimated shipping date: ${longDate(result.shipDate)}.`;
    // The countdown is shown only for a day or less of handling time (Lucas,
    // 2026-10-20, request 179): there the cutoff decides which day it is.
    // Longer handling time just gives the date.
    if (result.inWindow && days <= 1) return `Order within ${countdown()}. ${estimate}`;
    if (!days && cutoffLabel) return `${estimate} Orders placed by ${cutoffLabel} on a business day ship the same day.`;
    return estimate;
  }

  function paint(el, now) {
    const text = el.querySelector('[data-lead-time-text]');
    if (!text) return;
    try {
      const holidays = (el.dataset.holidays || '').split(',').map((s) => s.trim()).filter(Boolean);
      const result = compute(now || new Date(), {
        cutoff: el.dataset.cutoff,
        zone: el.dataset.zone,
        holidays,
        handleDays: el.dataset.handleDays,
      });
      text.textContent = describe(result, el.dataset.cutoffLabel);
      el.dataset.ships = result.shipsToday ? 'today' : result.handleDays ? 'later' : 'next-business-day';
    } catch {
      // An unknown zone or a malformed cutoff: the static sentence stays.
    }
  }

  function paintAll() {
    for (const el of document.querySelectorAll('[data-lead-time]')) paint(el);
  }

  document.addEventListener('DOMContentLoaded', paintAll);
  // Re-rendered cart markup carries the static sentence again. The drawer and
  // page swap in their listeners; this runs after them.
  document.addEventListener('cart:updated', () => setTimeout(paintAll, 0));
  setInterval(paintAll, 60000);

  window.LeadTime = { compute, describe, paint, longDate };
})();
